(() => {
  const quoteState = new WeakMap();
  const classNames = ["is-loading", "is-available", "is-unavailable", "is-quote-required", "is-estimate-only"];

  const parseVariants = (root) => {
    try {
      return JSON.parse(root.querySelector("[data-product-variants]")?.textContent || "[]");
    } catch (_error) {
      return [];
    }
  };

  const message = (quote, key, fallback) => quote.dataset[key] || fallback;

  const formatUsd = (minor) => {
    const value = Number(minor) / 100;
    if (!Number.isFinite(value)) return "";
    return `+US$${new Intl.NumberFormat(document.documentElement.lang || "en", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)}`;
  };

  const formatWeight = (value) => {
    const weight = Number(value);
    if (!Number.isFinite(weight)) return "";
    return new Intl.NumberFormat(document.documentElement.lang || "en", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(weight);
  };

  const quoteItems = (quote) => {
    if (quote.dataset.quoteContext === "cart") {
      const root = quote.closest(".cart-shell, [data-cart-quote-scope]");
      let serializedItems = [];
      try {
        serializedItems = JSON.parse(root?.querySelector("[data-cart-shipping-items]")?.textContent || "[]");
      } catch (_error) {
        serializedItems = [];
      }
      const quantityInputs = Array.from(root?.querySelectorAll("input[name='updates[]']") || []);
      const items = serializedItems
        .map((item, index) => {
          const quantity = Math.max(0, Math.round(Number(quantityInputs[index]?.value ?? item.quantity) || 0));
          const grams = Math.round(Number(item.grams));
          const variantId = Number(item.variant_id);
          return {
            quantity,
            grams,
            variant_id: Number.isSafeInteger(variantId) ? variantId : undefined,
            sku: typeof item.sku === "string" ? item.sku : undefined,
            requires_shipping: item.requires_shipping !== false,
          };
        })
        .filter((item) => item.quantity > 0 && item.requires_shipping);
      return items;
    }

    const root = quote.closest("[data-product-fitment-root]");
    if (!root) return [];
    const variants = parseVariants(root);
    const variantSelect = root.querySelector(".product-variant-master, .dorimm-product-form [name='id']");
    const variant = variants.find((item) => String(item.id) === String(variantSelect?.value)) || variants[0];
    const quantity = Math.max(1, Math.round(Number(root.querySelector("[data-quantity-value]")?.value) || 1));
    const grams = Math.round(Number(variant?.grams));
    const variantId = Number(variant?.id);
    return Number.isInteger(grams) && grams > 0
      ? [{
          quantity,
          grams,
          variant_id: Number.isSafeInteger(variantId) ? variantId : undefined,
          sku: typeof variant?.sku === "string" ? variant.sku : undefined,
          requires_shipping: true,
        }]
      : [];
  };

  const setState = (quote, state, price, status, options = {}) => {
    const card = quote.querySelector("[data-air-option]");
    const priceNode = quote.querySelector("[data-air-price]");
    const statusNode = quote.querySelector("[data-air-status]");
    const postcode = quote.querySelector("[data-air-postcode]");
    const quoteLink = quote.querySelector("[data-air-quote-link]");
    if (!card || !priceNode || !statusNode || !postcode || !quoteLink) return;
    card.classList.remove(...classNames);
    card.classList.add(state);
    card.setAttribute("aria-busy", state === "is-loading" ? "true" : "false");
    priceNode.textContent = price;
    statusNode.textContent = status;
    postcode.hidden = !options.postcode;
    quoteLink.hidden = !options.quoteLink;
  };

  const refreshQuote = async (quote) => {
    const endpoint = quote.dataset.estimateUrl;
    const country = (quote.dataset.countryCode || "").toUpperCase();
    const items = quoteItems(quote);
    if (!endpoint || !country || !items.length) {
      setState(
        quote,
        "is-unavailable",
        message(quote, "msgUnavailable", "Unavailable"),
        message(quote, "msgMissingWeight", "Packed shipping weight is unavailable."),
      );
      return;
    }

    const state = quoteState.get(quote) || {};
    state.controller?.abort();
    state.controller = new AbortController();
    quoteState.set(quote, state);
    setState(
      quote,
      "is-loading",
      message(quote, "msgCalculating", "Calculating…"),
      message(quote, "msgCalculating", "Calculating…"),
    );

    const postcodeInput = quote.querySelector("[data-air-postcode-input]");
    const postalCode = postcodeInput?.value.trim() || "";
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rate: {
            destination: { country_code: country, postal_code: postalCode },
            items,
          },
        }),
        signal: state.controller.signal,
      });
      if (!response.ok) throw new Error(`Estimate request failed: ${response.status}`);
      const payload = await response.json();
      if (payload.available) {
        const price = formatUsd(payload.total_price_minor);
        const weight = formatWeight(payload.chargeable_weight_kg ?? payload.billable_weight_kg);
        const chargeableWeight = message(quote, "msgChargeableWeight", "Air chargeable weight");
        const surcharge = message(quote, "msgAirSurcharge", "Air upgrade");
        const estimateOnly = Boolean(payload.estimate_only);
        setState(
          quote,
          estimateOnly ? "is-estimate-only" : "is-available",
          price || surcharge,
          estimateOnly
            ? `${message(quote, "msgEstimateOnly", "Non-guaranteed estimate; final route and price require postcode review.")} ${chargeableWeight}: ${weight} kg.`
            : `${surcharge} · ${chargeableWeight}: ${weight} kg`,
          { quoteLink: estimateOnly },
        );
        return;
      }

      if (payload.reason === "MISSING_DESTINATION_POSTAL_CODE") {
        setState(
          quote,
          "is-quote-required",
          message(quote, "msgPostcodeRequired", "Enter postcode"),
          message(quote, "msgPostcodeRequired", "Enter postcode for an exact Air price."),
          { postcode: true },
        );
        return;
      }
      if (payload.reason === "QUOTE_REQUIRED") {
        setState(
          quote,
          "is-quote-required",
          message(quote, "msgQuoteRequired", "Quote required"),
          message(quote, "msgQuoteRequired", "Air Express requires an individual route quote."),
          { quoteLink: true },
        );
        return;
      }
      if (payload.reason === "REMOTE_DESTINATION_REQUIRES_QUOTE") {
        setState(
          quote,
          "is-quote-required",
          message(quote, "msgQuoteRequired", "Quote required"),
          message(quote, "msgRemoteQuoteRequired", "This remote destination requires individual review."),
          { postcode: true, quoteLink: true },
        );
        return;
      }
      if (payload.reason === "MISSING_OR_INVALID_WEIGHT") {
        setState(
          quote,
          "is-unavailable",
          message(quote, "msgUnavailable", "Unavailable"),
          message(quote, "msgMissingWeight", "Packed shipping weight is unavailable."),
        );
        return;
      }
      setState(
        quote,
        "is-unavailable",
        message(quote, "msgUnavailable", "Not offered"),
        message(quote, "msgUnavailable", "Air Express is not offered to this destination."),
      );
    } catch (error) {
      if (error?.name === "AbortError") return;
      setState(
        quote,
        "is-unavailable",
        message(quote, "msgUnavailable", "Unavailable"),
        message(quote, "msgTemporaryUnavailable", "Air price is temporarily unavailable."),
      );
    }
  };

  const scheduleQuote = (quote, delay = 0) => {
    const state = quoteState.get(quote) || {};
    window.clearTimeout(state.timer);
    state.timer = window.setTimeout(() => refreshQuote(quote), delay);
    quoteState.set(quote, state);
  };

  const initQuote = (quote) => {
    if (quote.dataset.shippingQuoteReady === "true") return;
    quote.dataset.shippingQuoteReady = "true";
    const postcodeInput = quote.querySelector("[data-air-postcode-input]");
    const country = quote.dataset.countryCode || "";
    const storageKey = `dorimm-air-postcode-${country}`;
    try {
      if (postcodeInput) postcodeInput.value = window.sessionStorage.getItem(storageKey) || "";
    } catch (_error) {
      // Storage is optional; quoting still works without it.
    }
    postcodeInput?.addEventListener("input", () => {
      try { window.sessionStorage.setItem(storageKey, postcodeInput.value.trim()); } catch (_error) { /* optional */ }
      scheduleQuote(quote, 450);
    });
    const scope = quote.dataset.quoteContext === "product"
      ? quote.closest("[data-product-fitment-root]")
      : quote.closest(".cart-shell, [data-cart-quote-scope]");
    scope?.addEventListener("change", () => queueMicrotask(() => scheduleQuote(quote)));
    scope?.addEventListener("input", (event) => {
      if (event.target === postcodeInput) return;
      queueMicrotask(() => scheduleQuote(quote, 120));
    });
    scheduleQuote(quote);
  };

  const initialize = (scope = document) => scope.querySelectorAll?.("[data-shipping-quote]").forEach(initQuote);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => initialize());
  else initialize();
  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
})();
